import { CheckCircleIcon } from "@heroicons/react/20/solid"
import { useLocation } from "wouter-preact"
import { IntegrationConfig } from "./types"

interface IntegrationCardProps {
  config: IntegrationConfig
  connected?: boolean
  href: string
}

export function IntegrationCardButton({ config, connected, href }: IntegrationCardProps) {
  const [, setLocation] = useLocation()

  function handleClick() {
    if (config.comingSoon) return
    setLocation(href)
  }

  const showTextCard = !config.logo
  const showSubLogos = (config.subLogos?.length ?? 0) > 0

  return (
    <button
      type="button"
      onClick={handleClick}
      class={`group relative flex flex-col items-stretch justify-center p-5 bg-white border border-gray-200 rounded-lg transition-all min-h-[120px] w-full [perspective:800px] ${
        config.comingSoon ? "cursor-default" : "hover:border-gray-300 hover:shadow-sm cursor-pointer"
      }`}>
      {connected && (
        <div class="absolute top-2.5 right-2.5 z-10">
          <CheckCircleIcon class="h-4 w-4 text-green-500" />
        </div>
      )}
      {config.logo ? (
        // Many marks (co-op seals, monograms) don't say which retailer or distributor they are, so
        // logo cards flip on hover to reveal the name. Only the inner face rotates — the border,
        // checkmark and Coming Soon badge stay put.
        <div class="relative grow transition-transform duration-500 ease-in-out [transform-style:preserve-3d] group-hover:[transform:rotateY(180deg)] motion-reduce:duration-0">
          <div class="absolute inset-0 flex items-center justify-center [backface-visibility:hidden]">
            <img
              src={config.logo}
              alt={config.name}
              class={`${config.logoHeightClass ?? "h-10"} object-contain ${config.comingSoon ? "opacity-60" : "opacity-80"}`}
            />
          </div>
          <div class="absolute inset-0 flex flex-col items-center justify-center gap-1 text-center [backface-visibility:hidden] [transform:rotateY(180deg)]">
            <div class={`text-base font-semibold ${config.comingSoon ? "text-gray-400" : "text-gray-900"}`}>
              {config.name}
            </div>
            {config.description && (
              <div class={`text-xs ${config.comingSoon ? "text-gray-300" : "text-gray-500"}`}>{config.description}</div>
            )}
          </div>
        </div>
      ) : null}

      {showTextCard && (
        <div class="flex flex-col items-center text-center gap-1">
          {showSubLogos && (
            <div class="flex items-center justify-center gap-2 pb-1">
              {config.subLogos!.map((logo: { src: string; alt: string }) => (
                <img key={logo.src} src={logo.src} alt={logo.alt} class="h-6 w-6 object-contain opacity-90" />
              ))}
            </div>
          )}
          <div class={`text-base font-semibold ${config.comingSoon ? "text-gray-400" : "text-gray-900"}`}>{config.name}</div>
          {config.description && <div class={`text-xs ${config.comingSoon ? "text-gray-300" : "text-gray-500"}`}>{config.description}</div>}
        </div>
      )}
      {config.comingSoon && (
        <div class="absolute inset-0 z-10 flex items-end justify-center pb-3">
          <span class="px-2.5 py-1 bg-gray-900/80 text-white text-[10px] font-medium rounded-full backdrop-blur-sm">
            Coming Soon
          </span>
        </div>
      )}
    </button>
  )
}
